import * as React from 'react'
import { jt, t } from 'ttag'
import { GoalWithProgress, Lang, ProgressType } from '../../services/api.types'
import moment from 'moment'
import { getCustomTypeText, getCustomUnitText } from '../../services/CustomTypes'
import { getChallengeProgressUnits, isCustomType } from './challenges'
import { currentLang, currentLocale } from '../../i18nInit'
import { unitsCountDurationMinuteLong, unitsCountDurationMinuteShort, unitsCountEnergyCalorieLong } from './units'

export function formatThousands(num: number, minimumFractionDigits?: number): string {
  const value = num !== null && num !== undefined ? num : 0
  return value.toLocaleString(currentLocale, {
    maximumFractionDigits: minimumFractionDigits,
    minimumFractionDigits,
  })
}

export function getProgressTypeString(goal: GoalWithProgress, short?: boolean) {
  const {
    progressType,
    stepsGoal,
    activityFormulaTarget,
    dailyPersistenceGoal: dailyPersistenceGoalOrig,
    persistenceGoal,
    startDate,
    endDate,
    customType,
    customUnit,
    customName,
    customGoal,
    id,
  } = goal
  let { collectiveGoal } = goal
  const targetNumber = activityFormulaTarget || 100
  if (!collectiveGoal) collectiveGoal = 0
  let stepsNumber = formatThousands(progressType === ProgressType.COLLECTIVE_STEPS ? collectiveGoal : stepsGoal)
  const caloriesNumber = formatThousands(collectiveGoal)
  const minutesNumber = formatThousands(collectiveGoal)
  const collectiveGoalNumber = formatThousands(collectiveGoal)
  const dailyPersistenceGoal =
    progressType === ProgressType.PERSISTENCE_ALL
      ? dailyPersistenceGoalOrig
      : formatThousands(dailyPersistenceGoalOrig || 0)
  const daysTotal = moment(endDate).diff(moment(startDate), 'days') + 1

  const customTotal = formatThousands(dailyPersistenceGoalOrig || customGoal || 0) //daysTotal * dailyPersistenceGoalOrig

  const isCustom = isCustomType(progressType)
  const activity = isCustom ? getCustomTypeText(customType, customName) : ''
  //const unit = isCustom ? getCustomUnitText(customType, customType, customUnit) : ''
  const unit = getChallengeProgressUnits(goal)

  // For Bolt campaign.
  if (id === 'aa85857a-6f30-4ab2-a6a7-751cc96b3196' && (stepsGoal === 186004 || stepsGoal === 185978)) {
    stepsNumber = formatThousands(186000)
  }

  switch (progressType) {
    case ProgressType.ALL:
      return t`${targetNumber}% of activity`
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return t`${stepsNumber} steps`
    case ProgressType.COLLECTIVE_CALORIES:
      return `${caloriesNumber} ${unitsCountEnergyCalorieLong}`
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return `${minutesNumber} ${unitsCountDurationMinuteShort}`
    //return `${minutesNumber} ${unitsCountDurationMinuteLong}`
    case ProgressType.PERSISTENCE_STEPS:
      return t`${dailyPersistenceGoal} steps a day for ${persistenceGoal}/${daysTotal} days`
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return t`${dailyPersistenceGoal} minutes a day for ${persistenceGoal}/${daysTotal} days`
    case ProgressType.PERSISTENCE_ALL:
      return t`${dailyPersistenceGoal}% of activity a day for ${persistenceGoal}/${daysTotal} days`
    case ProgressType.CUSTOM:
    case ProgressType.INTENSIVE_MINUTES:
      return short ? `${customTotal} ${unit}` : `${activity} ${customTotal} ${unit}`
    case ProgressType.COLLECTIVE_CUSTOM:
      return `${collectiveGoalNumber} ${unit}`
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      if (short) {
        return `${dailyPersistenceGoal} ${unit}`
      } else {
        // translator: {Sleeping} {8} {h} a day for {15}/{30} days
        return t`${activity} ${dailyPersistenceGoal} ${unit} a day for ${persistenceGoal}/${daysTotal} days`
      }
    default:
      return 'x'
  }
}

export function getDailyGoalString({
  progressType,
  dailyPersistenceGoal: dailyPersistenceGoalOrig,
  customType,
  customUnit,
  customName,
  simpleManualEntry,
}: GoalWithProgress) {
  const dailyPersistenceGoal =
    progressType === ProgressType.PERSISTENCE_ALL
      ? dailyPersistenceGoalOrig
      : formatThousands(dailyPersistenceGoalOrig || 0)

  const isCustom = isCustomType(progressType)
  const activity = isCustom ? getCustomTypeText(customType, customName) : ''
  const unit = isCustom ? getCustomUnitText(customType, customType, customUnit) : ''

  switch (progressType) {
    case ProgressType.PERSISTENCE_STEPS:
      return `${dailyPersistenceGoal} ${t`steps`}`
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return `${dailyPersistenceGoal} ${unitsCountDurationMinuteShort}`
    //return `${dailyPersistenceGoal} ${unitsCountDurationMinuteLong}`
    case ProgressType.PERSISTENCE_ALL:
      return `${dailyPersistenceGoal}% ${t`of activity`}`
    case ProgressType.PERSISTENCE_CUSTOM:
      return simpleManualEntry ? `${activity}` : `${dailyPersistenceGoal} ${unit}`
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return `${activity}`
    default:
      return ''
  }
}

export function getFullName(firstName: string, lastName: string, email: string): string {
  return !firstName && !lastName ? email : `${firstName} ${lastName}`
}

export function getErrorMsg(reason?: string): JSX.Element {
  const email = currentLang === Lang.ET ? 'tugi@yumuuv.com' : 'support@yumuuv.com'
  const supportEmail = (
    <a key={'supportEmail'} href={'mailto:' + email}>
      {email}
    </a>
  )
  return (
    <div>
      {jt`Something went wrong. Please try again or contact our support at ${supportEmail}`}
      {reason ? (
        <>
          {' '}
          <i style={{ whiteSpace: 'nowrap' }}>({reason})</i>
        </>
      ) : (
        ''
      )}
    </div>
  )
}
